import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
  BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts';
import SearchableSelect, { SearchableSelectOption } from '../components/SearchableSelect';
import ProofCell from '../components/ProofCell';
import { parseProofs, serializeProofs, MAX_PROOFS } from '../utils/proofs';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Payment modes — same set as the Ledger Workspace (PaymentMode enum).
const MODE_OPTIONS: { value: string; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CARD', label: 'Card' },
  { value: 'OTHER', label: 'Other' },
];

// ── Types ────────────────────────────────────────────────────────────────────
interface Summary {
  month: number;
  year: number;
  opening: number;
  topups: number;
  cash_in: number;
  cash_out: number;
  expected_on_hand: number;
  last_counted: number | null;
  last_count_date: string | null;
  discrepancy: number | null;
}

interface Txn {
  id: number;
  transaction_date: string;
  amount: string | number;
  transaction_type: 'DEBIT' | 'CREDIT';
  payment_mode: string;
  particular?: string | null;
  remarks?: string | null;
  proof?: string | null;
  entry_time?: string | null;
  receiver?: string | null;
  manager?: string | null;
  ledger_category_name?: string | null;
  ledger_subcategory_name?: string | null;
}

interface DrawerEvent {
  id: number;
  event_date: string;
  event_type: 'OPENING' | 'TOPUP' | 'COUNT';
  amount: string | number;
  note?: string | null;
  created_at: string;
  manager?: string | null;
  // Top-up attribution — ids drive the edit form, names the log columns.
  // Blank on opening/count events.
  withdrawn_from_id?: number | null;
  sender_id?: number | null;
  receiver_id?: number | null;
  withdrawn_from_name?: string | null;
  sender_name?: string | null;
  receiver_name?: string | null;
}

type Opt = { id: number; name: string };

// ledger_party_options discriminators of the top-up dropdowns. Their own
// kinds, kept separate from the Ledger Workspace's TRANSFERRED_FROM / SENDER /
// RECEIVER lists: the drawer is topped up by a few people out of a couple of
// accounts, and merging those into the ledger's party lists would bury them.
type PartyKind = 'PETTY_WITHDRAWN_FROM' | 'PETTY_SENDER' | 'PETTY_RECEIVER';
const PARTY_KINDS: PartyKind[] = ['PETTY_WITHDRAWN_FROM', 'PETTY_SENDER', 'PETTY_RECEIVER'];

// ── Helpers ──────────────────────────────────────────────────────────────────
function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}
const pad2 = (n: number) => String(n).padStart(2, '0');
const ymOf = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const addMonth = (ym: string, delta: number) => {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${pad2((idx % 12) + 1)}`;
};
const labelOf = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};
const fmtFull = (n: number) => (n < 0 ? '-' : '') + '₹' + Math.round(Math.abs(n)).toLocaleString('en-IN');
const fmtL = (n: number) => {
  const a = Math.abs(n);
  if (a >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const monthsEndingAt = (ym: string, n: number) => Array.from({ length: n }, (_, i) => addMonth(ym, -(n - 1 - i)));
const monthsAfter = (ym: string, n: number) => Array.from({ length: n }, (_, i) => addMonth(ym, i + 1));
const firstDay = (ym: string) => `${ym}-01`;
const lastDay = (ym: string) => {
  const [yy, mm] = ym.split('-').map(Number);
  return `${ym}-${pad2(new Date(yy, mm, 0).getDate())}`;
};
const shortLabel = (ym: string) => {
  const [yy, mm] = ym.split('-').map(Number);
  return new Date(yy, mm - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

// Linear regression over an index series -> slope/intercept; predict clamped >= 0
function linfit(series: number[]): { slope: number; intercept: number } {
  const n = series.length;
  if (n === 0) return { slope: 0, intercept: 0 };
  const mx = (n - 1) / 2;
  const my = series.reduce((a, b) => a + b, 0) / n;
  let denom = 0, num = 0;
  for (let i = 0; i < n; i++) { denom += (i - mx) ** 2; num += (i - mx) * (series[i] - my); }
  const slope = denom === 0 ? 0 : num / denom;
  return { slope, intercept: my - slope * mx };
}
const predictAt = (fit: { slope: number; intercept: number }, i: number) => Math.max(fit.slope * i + fit.intercept, 0);

const INCOME_COLOR = '#16a34a';
const SPEND_COLOR = '#ef4444';
const TOPUP_COLOR = '#2563eb';
const HAND_COLOR = '#7c3aed';
const OPENING_COLOR = '#0d9488';
const INWARDS_COLOR = '#0284c7';

const SERIES_A_COLOR = '#2563eb';
const SERIES_B_COLOR = '#f97316';

// Categorical palette for the breakdown bar/pie charts (ledger / subledger slices).
const PALETTE = ['#2563eb', '#f97316', '#16a34a', '#a855f7', '#ef4444', '#0ea5e9', '#eab308', '#ec4899', '#14b8a6', '#8b5cf6', '#f43f5e', '#64748b'];
const HIST_MONTHS = 12;   // months of history used to fit the trend
const FCAST_MONTHS = 12;  // months projected forward

type Dim = 'category' | 'subcategory';
type SeriesSel = { dim: Dim; name: string };

// ── KPI Card ─────────────────────────────────────────────────────────────────
const KPICard: React.FC<{ label: string; value: string; color: string; note?: string }> = ({ label, value, color, note }) => (
  <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem 1.35rem', border: '1px solid #eef1f6', boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 14px 30px -22px rgba(15,23,42,0.35)', position: 'relative', overflow: 'hidden' }}>
    <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: color, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 }} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
      <span style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    </div>
    <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</div>
    {note && <div style={{ marginTop: 10, fontSize: '0.72rem', color: '#94a3b8' }}>{note}</div>}
  </div>
);

const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p: any) => p.value != null);
  if (!rows.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.85rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.8rem' }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: '#0f172a' }}>{label}</div>
      {rows.map((p: any) => (
        <div key={p.name} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: p.color || p.stroke }} />
          <span style={{ color: '#64748b' }}>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Main ─────────────────────────────────────────────────────────────────────
const PettyCash: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(ymOf(new Date()));
  const [summary, setSummary] = useState<Summary | null>(null);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [showTxns, setShowTxns] = useState(false);  // Cash Transactions hidden by default
  const [events, setEvents] = useState<DrawerEvent[]>([]);
  // Party lists behind the top-up dropdowns. Petty-cash-only lists — nothing is
  // read from or written to the ledger's own party dropdowns.
  const [partyOptions, setPartyOptions] = useState<Record<PartyKind, Opt[]>>(
    { PETTY_WITHDRAWN_FROM: [], PETTY_SENDER: [], PETTY_RECEIVER: [] }
  );
  const [loading, setLoading] = useState(true);

  // drawer event modal. evtEditId is the row being corrected — null when the
  // modal is adding a new event, which is what decides POST vs PATCH.
  const [evtModal, setEvtModal] = useState<null | 'OPENING' | 'TOPUP' | 'COUNT'>(null);
  const [evtForm, setEvtForm] = useState<any>(null);
  const [evtEditId, setEvtEditId] = useState<number | null>(null);
  const [deletingEvtId, setDeletingEvtId] = useState<number | null>(null);

  // compare & forecast
  const [histTxns, setHistTxns] = useState<Txn[]>([]);
  const [seriesA, setSeriesA] = useState<SeriesSel>({ dim: 'category', name: '' });
  const [seriesB, setSeriesB] = useState<SeriesSel>({ dim: 'category', name: '' });

  // spend breakdown graphs
  const [gPeriod, setGPeriod] = useState<'pick' | 'current' | 'year'>('current');
  const [gPickMonth, setGPickMonth] = useState<string>(selectedMonth);
  const [gCompare, setGCompare] = useState<'LvL' | 'SvS' | 'LvS'>('LvL');   // ledger vs ledger / subledger vs subledger / ledger→subledger
  const [gChart, setGChart] = useState<'bar' | 'pie'>('bar');
  const [gLedger, setGLedger] = useState<string>('');                       // chosen ledger for the Ledger→Subledger breakdown

  const [y, m] = selectedMonth.split('-').map(Number);
  const windowMonths = useMemo(() => monthsEndingAt(selectedMonth, HIST_MONTHS), [selectedMonth]);
  const futureMonths = useMemo(() => monthsAfter(selectedMonth, FCAST_MONTHS), [selectedMonth]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, txnRes, evtRes, histRes] = await Promise.all([
        axios.get(`${API}/api/financial/petty-cash/summary`, { params: { month: m, year: y }, headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/transactions`, { params: { month: m, year: y, paid_from_petty_cash: true }, headers: getAuthHeader() }),
        axios.get(`${API}/api/financial/petty-cash/events`, { params: { month: m, year: y }, headers: getAuthHeader() }),
        // 12-month window of petty-cash spend (DEBIT) used to fit the forecast trend
        axios.get(`${API}/api/financial/transactions`, {
          params: {
            date_from: firstDay(windowMonths[0]),
            date_to: `${lastDay(selectedMonth)}T23:59:59`,
            paid_from_petty_cash: true,
            transaction_type: 'DEBIT',
          },
          headers: getAuthHeader(),
        }),
      ]);
      setSummary(sumRes.data);
      setTxns(Array.isArray(txnRes.data) ? txnRes.data : []);
      setEvents(Array.isArray(evtRes.data) ? evtRes.data : []);
      setHistTxns(Array.isArray(histRes.data) ? histRes.data : []);
    } catch {
      toast.error('Failed to load petty cash data');
    } finally {
      setLoading(false);
    }
  }, [m, y]);

  const fetchPartyOptions = useCallback(async () => {
    try {
      const lists = await Promise.all(PARTY_KINDS.map(kind =>
        axios.get(`${API}/api/financial/party-options`, { params: { kind }, headers: getAuthHeader() })
      ));
      setPartyOptions(Object.fromEntries(
        PARTY_KINDS.map((kind, i) => [kind, lists[i].data || []])
      ) as Record<PartyKind, Opt[]>);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchPartyOptions(); }, [fetchPartyOptions]);

  // ── Compare & Forecast (cash spend, DEBIT) ──
  const amtN = (t: Txn) => Number(t.amount) || 0;
  const nameOf = (t: Txn, dim: Dim) =>
    dim === 'category' ? (t.ledger_category_name || 'Uncategorised') : (t.ledger_subcategory_name || 'No subcategory');

  const namesByTotal = (dim: Dim): string[] => {
    const tot: Record<string, number> = {};
    histTxns.forEach((t) => { const k = nameOf(t, dim); tot[k] = (tot[k] || 0) + amtN(t); });
    return Object.entries(tot).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  };
  const catNames = useMemo(() => namesByTotal('category'), [histTxns]); // eslint-disable-line react-hooks/exhaustive-deps
  const subNames = useMemo(() => namesByTotal('subcategory'), [histTxns]); // eslint-disable-line react-hooks/exhaustive-deps
  const namesFor = (dim: Dim) => (dim === 'category' ? catNames : subNames);

  // seed default selections once data arrives
  useEffect(() => {
    if (!seriesA.name && catNames.length) setSeriesA({ dim: 'category', name: catNames[0] });
    if (!seriesB.name && catNames.length) setSeriesB({ dim: 'category', name: catNames[1] || catNames[0] });
  }, [catNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyFor = (sel: SeriesSel): number[] =>
    windowMonths.map((ym) =>
      histTxns
        .filter((t) => String(t.transaction_date).slice(0, 7) === ym && nameOf(t, sel.dim) === sel.name)
        .reduce((a, t) => a + amtN(t), 0)
    );

  const chart = useMemo(() => {
    const aHist = monthlyFor(seriesA);
    const bHist = monthlyFor(seriesB);
    const aFit = linfit(aHist);
    const bFit = linfit(bHist);
    const rows: any[] = windowMonths.map((ym, i) => ({ label: shortLabel(ym), A: aHist[i], B: bHist[i], Af: null, Bf: null }));
    if (rows.length) { rows[rows.length - 1].Af = aHist[aHist.length - 1]; rows[rows.length - 1].Bf = bHist[bHist.length - 1]; }
    futureMonths.forEach((ym, i) => {
      const idx = HIST_MONTHS + i;
      rows.push({ label: shortLabel(ym), A: null, B: null, Af: Math.round(predictAt(aFit, idx)), Bf: Math.round(predictAt(bFit, idx)) });
    });
    const aFcast = futureMonths.reduce((s, _, i) => s + predictAt(aFit, HIST_MONTHS + i), 0);
    const bFcast = futureMonths.reduce((s, _, i) => s + predictAt(bFit, HIST_MONTHS + i), 0);
    return {
      rows, aFit, bFit,
      aHistTotal: aHist.reduce((x, z) => x + z, 0),
      bHistTotal: bHist.reduce((x, z) => x + z, 0),
      aFcast, bFcast,
      aAvg: aFcast / FCAST_MONTHS, bAvg: bFcast / FCAST_MONTHS,
      seamX: windowMonths.length - 1,
    };
  }, [histTxns, seriesA, seriesB, windowMonths, futureMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasHist = histTxns.length > 0;

  // ── Spend Breakdown (bar / pie) ──
  // Keep the Ledger→Subledger picker seeded with the top ledger.
  useEffect(() => { if (!gLedger && catNames.length) setGLedger(catNames[0]); }, [catNames]); // eslint-disable-line react-hooks/exhaustive-deps

  // Rows for the chosen period, drawn from the same 12-month cash-spend window.
  const gTxns = useMemo(() => {
    if (gPeriod === 'year') return histTxns;
    const ym = gPeriod === 'current' ? selectedMonth : gPickMonth;
    return histTxns.filter((t) => String(t.transaction_date).slice(0, 7) === ym);
  }, [histTxns, gPeriod, gPickMonth, selectedMonth]);

  const gData = useMemo(() => {
    const src = gCompare === 'LvS'
      ? gTxns.filter((t) => nameOf(t, 'category') === gLedger)
      : gTxns;
    const dim: Dim = gCompare === 'LvL' ? 'category' : 'subcategory';
    const tot: Record<string, number> = {};
    src.forEach((t) => { const k = nameOf(t, dim); tot[k] = (tot[k] || 0) + amtN(t); });
    return Object.entries(tot).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [gTxns, gCompare, gLedger]); // eslint-disable-line react-hooks/exhaustive-deps

  const gTotal = useMemo(() => gData.reduce((s, d) => s + d.value, 0), [gData]);
  const gPeriodLabel = gPeriod === 'year'
    ? `Last ${HIST_MONTHS} months`
    : labelOf(gPeriod === 'current' ? selectedMonth : gPickMonth);

  // Shared add-to-list handler for the three top-up party dropdowns — one
  // /party-options list per kind, refetched so the new name is selectable.
  const addParty = (kind: PartyKind, label: string) =>
    async (name: string): Promise<SearchableSelectOption | null> => {
      try {
        const res = await axios.post(`${API}/api/financial/party-options`,
          { kind, name: name.trim() }, { headers: getAuthHeader() });
        await fetchPartyOptions();
        toast.success(`${label} added`);
        return res.data?.id ? { id: res.data.id, name: res.data.name } : null;
      } catch (e: any) {
        toast.error(e?.response?.data?.detail || `Failed to add ${label.toLowerCase()}`);
        return null;
      }
    };

  // ── Drawer event entry ──
  const openEvt = (type: 'OPENING' | 'TOPUP' | 'COUNT') => {
    setEvtEditId(null);
    setEvtForm({
      event_date: new Date().toISOString().split('T')[0],
      amount: '', note: '',
      withdrawn_from_id: '', sender_id: '', receiver_id: '',
    });
    setEvtModal(type);
  };

  // Same modal, prefilled — a correction reads and edits exactly the fields
  // that were entered, rather than being re-typed from scratch.
  const openEditEvt = (e: DrawerEvent) => {
    setEvtEditId(e.id);
    setEvtForm({
      event_date: String(e.event_date).split('T')[0],
      amount: String(e.amount ?? ''),
      note: e.note || '',
      withdrawn_from_id: e.withdrawn_from_id ? String(e.withdrawn_from_id) : '',
      sender_id: e.sender_id ? String(e.sender_id) : '',
      receiver_id: e.receiver_id ? String(e.receiver_id) : '',
    });
    setEvtModal(e.event_type);
  };

  const saveEvt = async () => {
    const amt = parseFloat(evtForm.amount);
    if (isNaN(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    const payload = {
      event_date: evtForm.event_date,
      event_type: evtModal,
      amount: amt,
      note: evtForm.note || null,
      // manager is deliberately absent: the drawer form no longer collects it,
      // and omitting it from the PATCH leaves whatever an older entry recorded
      // intact rather than blanking it on an unrelated edit.
      // Only the top-up form collects these; the other two events leave them null.
      withdrawn_from_id: evtForm.withdrawn_from_id ? parseInt(evtForm.withdrawn_from_id) : null,
      sender_id: evtForm.sender_id ? parseInt(evtForm.sender_id) : null,
      receiver_id: evtForm.receiver_id ? parseInt(evtForm.receiver_id) : null,
    };
    try {
      if (evtEditId !== null) {
        await axios.patch(`${API}/api/financial/petty-cash/events/${evtEditId}`, payload, { headers: getAuthHeader() });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/api/financial/petty-cash/events`, payload, { headers: getAuthHeader() });
        toast.success(
          evtModal === 'OPENING' ? 'Opening balance set' :
          evtModal === 'TOPUP' ? 'Top-up recorded' : 'Cash count recorded'
        );
      }
      setEvtModal(null);
      setEvtEditId(null);
      fetchData();
    } catch {
      toast.error('Failed to save');
    }
  };

  // Deleting changes the drawer balance, so the confirm spells out the amount
  // rather than asking a generic "are you sure?".
  const deleteEvt = async (e: DrawerEvent) => {
    const label = { OPENING: 'opening balance', TOPUP: 'top-up', COUNT: 'cash count' }[e.event_type];
    if (!window.confirm(`Delete this ${label} of ${fmtFull(Number(e.amount))} dated ${fmtDate(e.event_date)}?\n\nThe drawer balance will be recalculated without it.`)) return;
    setDeletingEvtId(e.id);
    try {
      await axios.delete(`${API}/api/financial/petty-cash/events/${e.id}`, { headers: getAuthHeader() });
      toast.success('Entry deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingEvtId(null);
    }
  };

  // ── styles ──
  const s: Record<string, React.CSSProperties> = {
    page: { padding: '1.75rem 2.25rem', width: '100%', maxWidth: 'none', margin: 0, boxSizing: 'border-box', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
    title: { fontSize: '1.55rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' },
    sub: { fontSize: '0.85rem', color: '#64748b', marginTop: 3 },
    controls: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', background: 'white', border: '1px solid #eef1f6', borderRadius: 12, padding: '0.4rem 0.6rem', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' },
    input: { padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.85rem', background: 'white' },
    card: { background: 'white', borderRadius: 16, padding: '1.4rem 1.6rem', border: '1px solid #eef1f6', boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 16px 36px -26px rgba(15,23,42,0.4)' },
    cardTitle: { fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', marginBottom: '1rem' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1.1rem', marginBottom: '1.5rem' },
    navBtn: { padding: '0.35rem 0.65rem', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, color: '#475569' },
    btn: { padding: '0.5rem 1rem', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 },
    th: { padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 700, background: '#f8fafc', color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '0.55rem 0.75rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem', color: '#334155' },
    label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' },
    // Drawer-log row actions — same gradient pills as the Ledger Workspace's.
    rowBtnEdit: { background: 'linear-gradient(135deg,#0ea5e9,#2563eb)', color: 'white', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', marginRight: 4 },
    rowBtnDelete: { background: 'linear-gradient(135deg,#f43f5e,#e11d48)', color: 'white', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' },
    finput: { width: '100%', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.875rem', boxSizing: 'border-box', background: 'white' },
  };
  const atCurrentMonth = selectedMonth >= ymOf(new Date());

  // drawer flow step component
  const Flow: React.FC<{ label: string; value: number; color: string; op?: string }> = ({ label, value, color, op }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
      {op && <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#cbd5e1' }}>{op}</span>}
      <div style={{ minWidth: 120 }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: '1.15rem', fontWeight: 800, color }}>{fmtFull(value)}</div>
      </div>
    </div>
  );

  const disc = summary?.discrepancy ?? null;
  const discTone = disc === null ? 'neutral' : Math.abs(disc) < 1 ? 'good' : 'bad';
  const discBg = discTone === 'good' ? '#dcfce7' : discTone === 'bad' ? '#fee2e2' : '#f1f5f9';
  const discColor = discTone === 'good' ? '#15803d' : discTone === 'bad' ? '#b91c1c' : '#64748b';

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Petty Cash</div>
          <div style={s.sub}>Cash drawer — opening, top-ups, cash in/out &amp; reconciliation</div>
        </div>
        <div style={s.controls}>
          <button style={s.navBtn} title="Previous month" onClick={() => setSelectedMonth(addMonth(selectedMonth, -1))}>‹</button>
          <input type="month" value={selectedMonth} max={ymOf(new Date())}
            onChange={(e) => e.target.value && setSelectedMonth(e.target.value)} style={s.input} />
          <button style={{ ...s.navBtn, ...(atCurrentMonth ? { color: '#cbd5e1', cursor: 'not-allowed' } : {}) }}
            title="Next month" disabled={atCurrentMonth}
            onClick={() => setSelectedMonth(addMonth(selectedMonth, 1))}>›</button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Loading…</div>}

      {!loading && summary && (
        <>
          {/* KPI cards */}
          <div style={s.kpiGrid}>
            <KPICard label="Opening Balance" value={fmtFull(summary.opening)} color={OPENING_COLOR} note="Start of month" />
            <KPICard label="Total Top-Ups" value={fmtFull(summary.topups)} color={TOPUP_COLOR} note="Bank → drawer" />
            <KPICard label="Total Inwards" value={fmtFull(Number(summary.opening) + Number(summary.topups))} color={INWARDS_COLOR} note="Opening + Top-Ups" />
            <KPICard label="Cash Out (month)" value={fmtFull(summary.cash_out)} color={SPEND_COLOR} note="Cash payments" />
            <KPICard label="Cash In Hand" value={fmtFull(summary.expected_on_hand)} color={HAND_COLOR}
              note={summary.last_count_date ? `Last counted ${fmtDate(summary.last_count_date)}` : 'No count yet'} />
          </div>

          {/* Drawer panel */}
          <div style={{ ...s.card, marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem', flexWrap: 'wrap', gap: 12 }}>
              <div style={s.cardTitle as React.CSSProperties}>The Drawer — {labelOf(selectedMonth)}</div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button style={{ ...s.btn, background: '#eef2ff', color: TOPUP_COLOR }} onClick={() => openEvt('TOPUP')}>+ Add Top-up</button>
                <button style={{ ...s.btn, background: '#f3e8ff', color: HAND_COLOR }} onClick={() => openEvt('COUNT')}>Count Cash</button>
                <button style={{ ...s.btn, background: '#f1f5f9', color: '#475569' }} onClick={() => openEvt('OPENING')}>Set Opening</button>
              </div>
            </div>

            {/* Flow */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap', padding: '0.5rem 0 1.2rem' }}>
              <Flow label="Opening" value={summary.opening} color="#475569" />
              <Flow label="Top-ups" value={summary.topups} color={TOPUP_COLOR} op="+" />
              <Flow label="Cash Out" value={summary.cash_out} color={SPEND_COLOR} op="−" />
              <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#cbd5e1' }}>=</span>
              <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 12, padding: '0.7rem 1.1rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: HAND_COLOR, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Expected on Hand</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: HAND_COLOR }}>{fmtFull(summary.expected_on_hand)}</div>
              </div>
            </div>

            {/* Reconciliation strip */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', borderTop: '1px solid #f1f5f9', paddingTop: '1rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Expected</div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{fmtFull(summary.expected_on_hand)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Last Counted</div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                  {summary.last_counted !== null ? fmtFull(summary.last_counted) : '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Discrepancy</div>
                {disc === null ? (
                  <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Count cash to reconcile</div>
                ) : (
                  <span style={{ display: 'inline-block', marginTop: 2, fontSize: '0.82rem', fontWeight: 700, color: discColor, background: discBg, padding: '3px 10px', borderRadius: 999 }}>
                    {disc > 0 ? `+${fmtFull(disc)} surplus` : disc < 0 ? `${fmtFull(disc)} short` : 'Balanced'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Cash transactions */}
          <div style={{ ...s.card, marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 12 }}>
              <div style={s.cardTitle as React.CSSProperties}>Cash Transactions — {labelOf(selectedMonth)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>Petty-cash entries flow in from the Full Ledger</span>
                <button style={{ ...s.btn, background: '#f1f5f9', color: '#475569' }} onClick={() => setShowTxns(v => !v)}>
                  {showTxns ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {!showTxns ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                {txns.length === 0
                  ? 'No cash transactions this month.'
                  : <>{txns.length} transaction{txns.length === 1 ? '' : 's'} hidden — click <b>Show</b> to view.</>}
              </div>
            ) : txns.length === 0 ? (
              <div style={{ padding: '2.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                No cash transactions this month. Add a cash income or expense above.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Time</th>
                    <th style={s.th}>Type</th>
                    <th style={s.th}>Category</th>
                    <th style={s.th}>Particulars</th>
                    <th style={s.th}>Receiver</th>
                    <th style={s.th}>Manager</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Proof</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => {
                    const isCredit = t.transaction_type === 'CREDIT';
                    return (
                      <tr key={t.id}>
                        <td style={s.td}>{fmtDate(t.transaction_date)}</td>
                        <td style={{ ...s.td, whiteSpace: 'nowrap', color: '#64748b' }}>{t.entry_time || '—'}</td>
                        <td style={s.td}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: isCredit ? INCOME_COLOR : SPEND_COLOR, background: isCredit ? '#dcfce7' : '#fee2e2', padding: '2px 8px', borderRadius: 999 }}>
                            {isCredit ? 'IN' : 'OUT'}
                          </span>
                        </td>
                        <td style={s.td}>
                          {t.ledger_category_name || '—'}
                          {t.ledger_subcategory_name && <span style={{ color: '#94a3b8' }}> · {t.ledger_subcategory_name}</span>}
                        </td>
                        <td style={s.td}>{t.particular || t.remarks || '—'}</td>
                        <td style={s.td}>{t.receiver || '—'}</td>
                        <td style={s.td}>{t.manager || '—'}</td>
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: isCredit ? INCOME_COLOR : SPEND_COLOR }}>
                          {isCredit ? '+' : '−'}{fmtFull(Number(t.amount))}
                        </td>
                        <td style={{ ...s.td, textAlign: 'center' }}>
                          <ProofCell value={t.proof} thumbnailSize={24} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Compare & Forecast */}
          <div style={{ ...s.card, marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={s.cardTitle as React.CSSProperties}>Compare &amp; Forecast — cash spend</div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  Last {HIST_MONTHS} months (solid) + {FCAST_MONTHS}-month linear-trend projection (dashed)
                </div>
              </div>
              {/* Series pickers */}
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                {([['A', seriesA, setSeriesA, SERIES_A_COLOR], ['B', seriesB, setSeriesB, SERIES_B_COLOR]] as const).map(
                  ([key, sel, setSel, color]) => (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Series {key}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select style={{ ...s.input, padding: '0.35rem 0.5rem' }} value={sel.dim}
                          onChange={(e) => { const dim = e.target.value as Dim; setSel({ dim, name: namesFor(dim)[key === 'A' ? 0 : Math.min(1, namesFor(dim).length - 1)] || '' }); }}>
                          <option value="category">Category</option>
                          <option value="subcategory">Subcategory</option>
                        </select>
                        <select style={{ ...s.input, padding: '0.35rem 0.5rem', minWidth: 150 }} value={sel.name}
                          onChange={(e) => setSel({ ...sel, name: e.target.value })}>
                          {namesFor(sel.dim).length === 0 && <option value="">— no data —</option>}
                          {namesFor(sel.dim).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            {!hasHist ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                No cash spend in the last {HIST_MONTHS} months yet. Add cash expenses and the comparison &amp; forecast will build here.
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={chart.rows} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} interval={0} angle={-30} textAnchor="end" height={48} />
                    <YAxis tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip content={<ChartTip />} />
                    <Legend wrapperStyle={{ fontSize: '0.8rem', paddingTop: '0.5rem' }} />
                    <ReferenceLine x={chart.rows[chart.seamX]?.label} stroke="#cbd5e1" strokeDasharray="4 4" label={{ value: 'now', fontSize: 10, fill: '#94a3b8', position: 'top' }} />
                    <Line dataKey="A" name={`${seriesA.name} (actual)`} stroke={SERIES_A_COLOR} strokeWidth={2.4} dot={{ r: 2.5 }} connectNulls={false} />
                    <Line dataKey="Af" name={`${seriesA.name} (projected)`} stroke={SERIES_A_COLOR} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} />
                    <Line dataKey="B" name={`${seriesB.name} (actual)`} stroke={SERIES_B_COLOR} strokeWidth={2.4} dot={{ r: 2.5 }} connectNulls={false} />
                    <Line dataKey="Bf" name={`${seriesB.name} (projected)`} stroke={SERIES_B_COLOR} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls={false} />
                  </ComposedChart>
                </ResponsiveContainer>

                {/* Forecast summary tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                  {([['A', seriesA, SERIES_A_COLOR, chart.aHistTotal, chart.aFcast, chart.aAvg, chart.aFit.slope],
                     ['B', seriesB, SERIES_B_COLOR, chart.bHistTotal, chart.bFcast, chart.bAvg, chart.bFit.slope]] as const).map(
                    ([key, sel, color, histTotal, fcast, avg, slope]) => (
                      <div key={key} style={{ border: `1px solid #eef1f6`, borderLeft: `4px solid ${color}`, borderRadius: 12, padding: '0.9rem 1.1rem' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                          {sel.name} <span style={{ fontWeight: 500, color: '#94a3b8', fontSize: '0.72rem' }}>· {sel.dim}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748b', padding: '2px 0' }}>
                          <span>Last {HIST_MONTHS} mo</span><span style={{ fontWeight: 700, color: '#0f172a' }}>{fmtFull(histTotal)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748b', padding: '2px 0' }}>
                          <span>Projected next {FCAST_MONTHS} mo</span><span style={{ fontWeight: 700, color }}>{fmtFull(fcast)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748b', padding: '2px 0' }}>
                          <span>Projected avg / mo</span><span style={{ fontWeight: 700, color: '#0f172a' }}>{fmtFull(avg)}</span>
                        </div>
                        <div style={{ marginTop: 6, fontSize: '0.74rem', fontWeight: 700, color: slope > 0.5 ? '#dc2626' : slope < -0.5 ? '#16a34a' : '#64748b' }}>
                          {slope > 0.5 ? '▲ trending up' : slope < -0.5 ? '▼ trending down' : '▬ flat'}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </>
            )}
          </div>

          {/* Spend Breakdown — bar / pie */}
          <div style={{ ...s.card, marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={s.cardTitle as React.CSSProperties}>Spend Breakdown — cash spend</div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  {gPeriodLabel} · {gCompare === 'LvL' ? 'ledger vs ledger' : gCompare === 'SvS' ? 'subledger vs subledger' : `subledgers within ${gLedger || '—'}`} · total {fmtFull(gTotal)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* Period */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Period</span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {([['current', 'Current Month'], ['year', 'Last 12 Months'], ['pick', 'Pick Month']] as const).map(([val, lbl]) => (
                      <button key={val} onClick={() => setGPeriod(val)}
                        style={{ ...s.navBtn, fontSize: '0.78rem', fontWeight: 600, padding: '0.35rem 0.7rem',
                          ...(gPeriod === val ? { background: '#2563eb', color: 'white', borderColor: '#2563eb' } : {}) }}>
                        {lbl}
                      </button>
                    ))}
                    {gPeriod === 'pick' && (
                      <select style={{ ...s.input, padding: '0.35rem 0.5rem' }} value={gPickMonth} onChange={(e) => setGPickMonth(e.target.value)}>
                        {windowMonths.map((ym) => <option key={ym} value={ym}>{labelOf(ym)}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                {/* Comparison */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Compare</span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {([['LvL', 'By Ledger'], ['SvS', 'By Subledger'], ['LvS', 'Ledger → Subledger']] as const).map(([val, lbl]) => (
                      <button key={val} onClick={() => setGCompare(val)}
                        style={{ ...s.navBtn, fontSize: '0.78rem', fontWeight: 600, padding: '0.35rem 0.7rem',
                          ...(gCompare === val ? { background: '#0f172a', color: 'white', borderColor: '#0f172a' } : {}) }}>
                        {lbl}
                      </button>
                    ))}
                    {gCompare === 'LvS' && (
                      <select style={{ ...s.input, padding: '0.35rem 0.5rem', minWidth: 140 }} value={gLedger} onChange={(e) => setGLedger(e.target.value)}>
                        {catNames.length === 0 && <option value="">— no data —</option>}
                        {catNames.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                {/* Chart type */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Chart</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {([['bar', 'Bar'], ['pie', 'Pie']] as const).map(([val, lbl]) => (
                      <button key={val} onClick={() => setGChart(val)}
                        style={{ ...s.navBtn, fontSize: '0.78rem', fontWeight: 600, padding: '0.35rem 0.7rem',
                          ...(gChart === val ? { background: '#7c3aed', color: 'white', borderColor: '#7c3aed' } : {}) }}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {gData.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                No cash spend for this selection.
              </div>
            ) : gChart === 'bar' ? (
              <ResponsiveContainer width="100%" height={Math.max(260, gData.length * 34 + 40)}>
                <BarChart data={gData} layout="vertical" margin={{ top: 8, right: 48, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v: any) => fmtFull(Number(v))} cursor={{ fill: '#f8fafc' }} />
                  <Bar dataKey="value" name="Cash spend" radius={[0, 6, 6, 0]}>
                    {gData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <PieChart>
                  <Pie data={gData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={130}
                    label={(e: any) => `${e.name} · ${gTotal ? Math.round((e.value / gTotal) * 100) : 0}%`}
                    labelLine={false}>
                    {gData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => fmtFull(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Drawer log */}
          <div style={s.card}>
            <div style={s.cardTitle as React.CSSProperties}>Drawer Log — opening, top-ups &amp; counts</div>
            {events.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
                No drawer events this month.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, width: 56 }}>Sl No.</th>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Event</th>
                    <th style={s.th}>Withdrawn From</th>
                    <th style={s.th}>Sender</th>
                    <th style={s.th}>Receiver</th>
                    <th style={s.th}>Note</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                    <th style={{ ...s.th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e, i) => {
                    const cfg = {
                      OPENING: { label: 'Opening', color: '#475569', bg: '#f1f5f9' },
                      TOPUP: { label: 'Top-up', color: TOPUP_COLOR, bg: '#eef2ff' },
                      COUNT: { label: 'Cash Count', color: HAND_COLOR, bg: '#f3e8ff' },
                    }[e.event_type];
                    return (
                      <tr key={e.id}>
                        {/* Position in this month's log, newest first — the row
                            you point at when saying "delete number 3". */}
                        <td style={{ ...s.td, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                        <td style={s.td}>{fmtDate(e.event_date)}</td>
                        <td style={s.td}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '2px 8px', borderRadius: 999 }}>{cfg.label}</span>
                        </td>
                        <td style={s.td}>{e.withdrawn_from_name || '—'}</td>
                        <td style={s.td}>{e.sender_name || '—'}</td>
                        <td style={s.td}>{e.receiver_name || '—'}</td>
                        <td style={s.td}>{e.note || '—'}</td>
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtFull(Number(e.amount))}</td>
                        <td style={{ ...s.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button style={s.rowBtnEdit} onClick={() => openEditEvt(e)}>Edit</button>
                          <button style={{ ...s.rowBtnDelete, opacity: deletingEvtId === e.id ? 0.5 : 1 }}
                            disabled={deletingEvtId === e.id} onClick={() => deleteEvt(e)}>
                            {deletingEvtId === e.id ? '…' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}


      {/* ── Drawer event modal ── */}
      {evtModal && evtForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '8vh' }}>
          {/* Scrolls rather than running off-screen: the top-up form is four
              dropdowns taller than the opening/count ones. */}
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: 14, width: 460, maxWidth: '96vw', maxHeight: '84vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                {evtEditId !== null
                  ? (evtModal === 'OPENING' ? 'Edit Opening Balance' : evtModal === 'TOPUP' ? 'Edit Top-up' : 'Edit Cash Count')
                  : (evtModal === 'OPENING' ? 'Set Opening Balance' : evtModal === 'TOPUP' ? 'Add Top-up' : 'Count Cash')}
              </h2>
              <button onClick={() => { setEvtModal(null); setEvtEditId(null); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 1.2rem' }}>
              {evtEditId !== null
                ? 'Correcting a recorded entry — the drawer balance recalculates from the saved values.'
                : <>
                    {evtModal === 'OPENING' && 'Starting cash placed in the drawer.'}
                    {evtModal === 'TOPUP' && 'Cash moved from bank into the drawer (a transfer — not income).'}
                    {evtModal === 'COUNT' && 'Enter the physically counted cash to reconcile against the expected balance.'}
                  </>}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={s.label}>Date</label>
                <input type="date" style={s.finput} value={evtForm.event_date} onChange={(e) => setEvtForm({ ...evtForm, event_date: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>{evtModal === 'COUNT' ? 'Counted (₹)' : 'Amount (₹)'}</label>
                <input type="number" step="0.01" style={{ ...s.finput, textAlign: 'right' }} placeholder="0.00" value={evtForm.amount} onChange={(e) => setEvtForm({ ...evtForm, amount: e.target.value })} />
              </div>
            </div>

            {evtModal === 'COUNT' && summary && (
              <div style={{ background: '#f8fafc', border: '1px solid #eef1f6', borderRadius: 10, padding: '0.7rem 0.9rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#475569' }}>
                Expected on hand: <strong>{fmtFull(summary.expected_on_hand)}</strong>
                {evtForm.amount !== '' && !isNaN(parseFloat(evtForm.amount)) && (
                  <span> · Difference: <strong style={{ color: parseFloat(evtForm.amount) - summary.expected_on_hand < 0 ? '#b91c1c' : '#15803d' }}>
                    {fmtFull(parseFloat(evtForm.amount) - summary.expected_on_hand)}
                  </strong></span>
                )}
              </div>
            )}

            {evtModal === 'TOPUP' && (
              <>
                {/* Withdrawn From / Sender / Receiver are petty-cash-only
                    lists — they neither read from nor write to the Ledger
                    Workspace's party dropdowns. */}
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={s.label}>Withdrawn From <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                  <SearchableSelect options={partyOptions.PETTY_WITHDRAWN_FROM as SearchableSelectOption[]}
                    value={String(evtForm.withdrawn_from_id || '')}
                    onChange={(val) => setEvtForm({ ...evtForm, withdrawn_from_id: val })}
                    onAddNew={addParty('PETTY_WITHDRAWN_FROM', 'Account')}
                    placeholder="Select account…" addNewLabel="account" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <div>
                    <label style={s.label}>Sender <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                    <SearchableSelect options={partyOptions.PETTY_SENDER as SearchableSelectOption[]}
                      value={String(evtForm.sender_id || '')}
                      onChange={(val) => setEvtForm({ ...evtForm, sender_id: val })}
                      onAddNew={addParty('PETTY_SENDER', 'Sender')}
                      placeholder="Select sender…" addNewLabel="sender" />
                  </div>
                  <div>
                    <label style={s.label}>Receiver <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                    <SearchableSelect options={partyOptions.PETTY_RECEIVER as SearchableSelectOption[]}
                      value={String(evtForm.receiver_id || '')}
                      onChange={(val) => setEvtForm({ ...evtForm, receiver_id: val })}
                      onAddNew={addParty('PETTY_RECEIVER', 'Receiver')}
                      placeholder="Select receiver…" addNewLabel="receiver" />
                  </div>
                </div>
              </>
            )}

            {/* Note sits last on every one of the three forms — it's the
                free-text afterthought, not something to wade through on the
                way to the fields that matter. */}
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={s.label}>Note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <input type="text" style={s.finput} placeholder="e.g. withdrew from HDFC" value={evtForm.note} onChange={(e) => setEvtForm({ ...evtForm, note: e.target.value })} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '1.2rem', borderTop: '1px solid #f1f5f9', paddingTop: '1rem' }}>
              <button style={{ ...s.btn, background: '#f1f5f9', color: '#475569' }} onClick={() => { setEvtModal(null); setEvtEditId(null); }}>Cancel</button>
              <button style={{ ...s.btn, background: '#2563eb', color: 'white' }} onClick={saveEvt}>
                {evtEditId !== null ? 'Save Changes' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PettyCash;
